import type { ErrorEvent } from "@sentry/vue";

import { useLogger } from "@blackfisk/vue/composables/useLogger";
import { configureSync, getConsoleSink } from "@logtape/logtape";
import { getSentrySink } from "@logtape/sentry";

configureSync({
  sinks: {
    console: getConsoleSink(),
    sentry: getSentrySink(),
  },
  loggers: [
    {
      category: ["client-frontend"],
      lowestLevel: import.meta.env.DEV ? "debug" : "info",
      sinks: ["console"],
    },
    { category: [], lowestLevel: "error", sinks: ["sentry"] },
  ],
});

const logger = useLogger("client-frontend", "app");

// oxlint-disable-next-line import/no-unassigned-import — CSS imports are side-effect imports
import "unfonts.css";
// oxlint-disable-next-line import/no-unassigned-import — CSS imports are side-effect imports
import "@/assets/main.css";
// oxlint-disable-next-line import/no-unassigned-import — CSS imports are side-effect imports
import "primeicons/primeicons.css";
// oxlint-disable-next-line import/no-unassigned-import — CSS imports are side-effect imports
import "@vue-js-cron/prime/dist/prime.css";
// oxlint-disable-next-line import/no-unassigned-import — CSS imports are side-effect imports
import "md-editor-v3/lib/style.css";

import { autoAnimatePlugin } from "@formkit/auto-animate/vue";
import { createPinia } from "pinia";
import piniaPluginPersistedstate from "pinia-plugin-persistedstate";
import { createApp } from "vue";

import App from "./App.vue";
import { isLocalEnvironment } from "./environment";
import { registerLogRocket } from "./logRocket";
import routeAuthentication from "./plugins/routeAuthentication";
import { registerPrimeVue } from "./primeVue";
import router from "./router";
import { registerSentry, enrichEventWithLogRocket } from "./sentry";
import { useIdentityStore } from "./stores/identity";
import { useLoadingStore } from "./stores/loading";

const app = createApp(App);
app.provide("VUE_APP_V2_BASE_URL", import.meta.env.VITE_BFSK_APP_V2_BASE_URL);

registerPrimeVue(app);
const pinia = createPinia();
pinia.use(piniaPluginPersistedstate);
app.use(pinia);
app.use(routeAuthentication);
app.use(router);
app.use(autoAnimatePlugin);

// ! Router Loading State
const LoadingStore = useLoadingStore();
router.beforeEach((to, from) => {
  if (to.path === from.path && to.hash === from.hash) {
    return;
  }
  LoadingStore.startLoader("page-load");
});
router.afterEach(() => {
  setTimeout(() => {
    LoadingStore.stopLoader("page-load");
  }, 250);
});

// ! Router Error Handling
router.onError((err) => {
  logger.error("Router error: {error}", { error: err });
});

// ! Global Error Handler
// * One capture point for every uncaught component error: the module boundaries
//   (`useModuleErrorBoundary`) toast and re-propagate, so the original error lands
//   here with the failing component's context. Log at `warn` (a breadcrumb) — an
//   `error` record would be captured by the LogTape → Sentry sink as a second event.
let SentryClient: ReturnType<typeof registerSentry> | null = null;
app.config.errorHandler = (err, instance, info): void => {
  logger.warn("Global Error: {error}", { error: err });
  // * `<script setup>` components carry their name on the compiler-added `__name`.
  const componentName =
    instance?.$options.name ??
    (instance?.$options as { __name?: string } | undefined)?.__name ??
    "unknown";
  SentryClient?.captureException(err, {
    contexts: {
      vue: {
        componentName,
        lifecycleHook: info,
      },
    },
  });
};

if (!isLocalEnvironment) {
  const LogRocket = registerLogRocket();
  SentryClient = registerSentry(app, {
    beforeSend: (event: ErrorEvent) => enrichEventWithLogRocket(event, LogRocket.sessionURL),
  });
}

// ! Boot rehydration: exchange the HttpOnly refresh cookie (if present) for fresh tokens and the
//   current profile before mounting, so the first guarded route reflects the real session.
const boot = async (): Promise<void> => {
  const IdentityStore = useIdentityStore();
  await IdentityStore.rehydrate();
  // * Expiry is checked on tab focus: if the access token has expired, refresh it (or collapse to
  //   login if the refresh is gone). No background timer — a token only matters while the user is
  //   actually looking at the tab, so check on focus/visibility rather than on a clock.
  window.addEventListener("focus", IdentityStore.handleFocusRefresh);
  window.addEventListener("visibilitychange", IdentityStore.handleFocusRefresh);
  app.mount("#app");
};

void boot();
